import React, {
  ReactElement, useEffect, useRef, useState,
} from 'react';
import { ConfirmModal, TextEditor } from 'slovlex-idsk-react-components';
import {
  Button, Col, ErrorMessage, FormGroup, Input, LoaderButton, Row,
} from 'idsk-react-components';
import { useNavigate, useParams } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import LegislativnyMaterialDetail
  from '../../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import { NavrhUzneseniaVladyFormData }
  from '../../../../model/sprievodnyDokument/navrhUzneseniaVlady/NavrhUzneseniaVladyFormData';
import FormElementWrapper from '../../../FormElements/FormElementWrapper';
import useFieldErrorMessages from '../../../useFieldErrorMessages';
import CastComponent, { dropdownValues } from './components/CastComponent';
import { NavrhUzneseniaVladyCast }
  from '../../../../model/sprievodnyDokument/navrhUzneseniaVlady/NavrhUzneseniaVladyCast';
import convertIndexToChar from '../../../../function/convertIndexToChar';
import './navrhUzneseniaVlady.scss';
import { removeFieldAtIndex, updateFieldAtIndex } from '../../../../function/updateArrayField';
import routes from '../../../../routing/routes';
import { useClient } from '../../../../context/ClientStatusContext';
import Uuid from '../../../../model/Uuid';
import { ClientResponse } from '../../../../model/ClientResponse';
import { useSuccessMessageContext } from '../../../../context/SuccessMessageContext';
import LoadNavrhUzneseniaDataButton from './components/LoadNavrhUzneseniaDataButton';
import { useUserContext } from '../../../../context/UserContext';
import { pouzivatelIsAdmin } from '../../../../function/permissions';

export interface Props {
  material: LegislativnyMaterialDetail;
  initialData: NavrhUzneseniaVladyFormData;
  onSubmit: (uuid: Uuid, form: NavrhUzneseniaVladyFormData) => Promise<ClientResponse<void>>
}

const NavrhUzneseniaVlady = ({ material, initialData, onSubmit }: Props): ReactElement => {
  const castContainerRef = useRef<HTMLDivElement>(null);
  const { getFieldErrorMessage } = useFieldErrorMessages();
  const [initFormData, setInitFormData] = useState<NavrhUzneseniaVladyFormData>(initialData);
  const [formData, setFormData] = useState<NavrhUzneseniaVladyFormData>(initFormData);
  const { displaySuccess } = useSuccessMessageContext();
  const navigate = useNavigate();
  const {
    type,
    year,
    index,
    stadiumUuid,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, stadiumUuid: string }>();
  const { pouzivatel } = useUserContext();

  const updateCastList = (castList: NavrhUzneseniaVladyCast[]): void => {
    setFormData({ ...formData, castList });
  };

  const addCast = (): void => {
    updateCastList([...formData.castList, {
      uuid: uuidv4(),
      bodList: [],
      zamerCasti: dropdownValues.find((item) => item === 'schvaľuje'),
    }]);

    if (castContainerRef?.current?.lastElementChild) {
      setTimeout(() => {
        castContainerRef.current.lastElementChild.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 100);
    }
  };

  const {
    wrapClient: submitSpravaoUcastiVerejnosti,
    status,
  } = useClient(onSubmit);

  const onSubmitForm = async (): Promise<void> => {
    await submitSpravaoUcastiVerejnosti(material.uuid, formData, stadiumUuid);
    navigate(
      pouzivatelIsAdmin(pouzivatel)
        ? routes.procesy.detail.sprievodneDokumenty.editAsAdmin.withParams(type, year, index, stadiumUuid)
        : routes.procesy.detail.sprievodneDokumenty.edit.withParams(type, year, index),
    );
    displaySuccess('Dokument bol úspešne uložený');
  };

  useEffect(() => {
    setFormData(initFormData);
  }, [initFormData]);

  return (
    <>
      {formData.isUznesenie && (
        <LoadNavrhUzneseniaDataButton
          setInitFormData={setInitFormData}
          stadiumUuid={stadiumUuid}
          legMaterialUuid={material.uuid}
        />
      )}
      <FormElementWrapper
        label="Nadpis - názov procesu"
        fieldName="nazovMaterialu"
        getFieldErrorMessage={getFieldErrorMessage}
        width="full"
      >
        {getFieldErrorMessage('nazovMaterialu')
          && <ErrorMessage message={getFieldErrorMessage('nazovMaterialu')} />}
        <TextEditor
          initialState={initFormData.nazovMaterialu}
          onChange={(data: string) => setFormData({ ...formData, nazovMaterialu: data })}
          maxLength={2200}
          showRemaining
        />
      </FormElementWrapper>
      <FormElementWrapper
        label="Číslo materiálu"
        fieldName="cisloMaterialu"
        getFieldErrorMessage={getFieldErrorMessage}
        width="one-half"
      >
        <Input
          type="text"
          value={formData.cisloMaterialu || ''}
          onChange={(event) => setFormData({ ...formData, cisloMaterialu: event.target.value })}
          maxLength={256}
        />
      </FormElementWrapper>
      <FormElementWrapper
        label="Predkladateľ"
        fieldName="predkladatel"
        getFieldErrorMessage={getFieldErrorMessage}
        width="one-half"
      >
        <Input
          type="text"
          value={formData.predkladatel}
          onChange={(event) => setFormData({ ...formData, predkladatel: event.target.value })}
          maxLength={256}
        />
      </FormElementWrapper>
      <Row>
        <Col>
          <Button
            disabled={formData.castList.length >= 26}
            type="button"
            buttonStyle="third"
            size="large"
            onClick={() => addCast()}
          >Pridať časť
          </Button>
        </Col>
      </Row>
      {formData.castList.length > 0
      && (
        <div className="casti-list-container">
          {formData.castList.map((castItem, castIndex) => (
            <div key={castItem.uuid} ref={castContainerRef} className="cast-component">
              <CastComponent
                castNazov={convertIndexToChar(castIndex)}
                castData={castItem}
                setCastData={castValue => updateCastList(updateFieldAtIndex(formData.castList, castValue, castIndex))}
                onDeleteCast={() => {
                  updateCastList(removeFieldAtIndex(formData.castList, castIndex));
                }}
              />
            </div>
          ))}
        </div>
      )}
      <FormElementWrapper
        label="Vykonajú"
        fieldName="vykonaju"
        getFieldErrorMessage={getFieldErrorMessage}
        width="full"
      >
        {getFieldErrorMessage('vykonaju')
          && <ErrorMessage message={getFieldErrorMessage('vykonaju')} />}
        <TextEditor
          initialState={initFormData.vykonaju}
          onChange={(data: string) => setFormData({ ...formData, vykonaju: data })}
          maxLength={2048}
          showRemaining
        />
      </FormElementWrapper>
      <FormElementWrapper
        label="Na vedomie"
        fieldName="naVedomie"
        getFieldErrorMessage={getFieldErrorMessage}
        width="full"
      >
        {getFieldErrorMessage('naVedomie')
          && <ErrorMessage message={getFieldErrorMessage('naVedomie')} />}
        <TextEditor
          initialState={initFormData.naVedomie}
          onChange={(data: string) => setFormData({ ...formData, naVedomie: data })}
          maxLength={2048}
          showRemaining
        />
      </FormElementWrapper>
      <FormGroup className="navrh-uznesenia__form-buttons">
        <ConfirmModal
          question="Naozaj si prajete uložiť formulár a vytvoriť sprievodný dokument?"
          description={'Ak upravujete existujúci formulár, budú údaje tohto formulára prepísané '
            + 'a výsledný dokument, ktorý vznikol na základe tohto formulára, bude nahradený.'}
          cancelButtonText="Nie"
          confirmButton={
            (
              <Button
                buttonStyle="action"
                size="large"
              >
                Áno
              </Button>
            )
          }
          openButton={(
            <LoaderButton
              loading={status.loading}
              type="submit"
              size="large"
              disableOutsideClickWhileLoading
            >Uložiť
            </LoaderButton>
          )}
          doAfterConfirm={() => onSubmitForm()}
        />
        <Button
          className="navrh-uznesenia-vlady__buttons"
          type="button"
          buttonStyle="third"
          size="large"
          disabled={status.loading}
          onClick={() => navigate(-1)}
        >Späť
        </Button>
      </FormGroup>
    </>
  );
};

export default NavrhUzneseniaVlady;
